import { TemplateElement, html, defineElement, when } from '@webtides/element-js';
import '../../../icon/icon.js';
import '../../../accordion-group/accordion-group.js';
import '../../../accordion-item/accordion-item.js';
import style from './clustered-links.css';

/**
 * @typedef {Object} ClusteredLink
 * @property {string} type
 * @property {string} url
 * @property {string} text
 * @property {string} [trackingLabel]
 * @property {string} [cssClasses]
 */

/**
 * @typedef {Object} ClusteredLinksItem
 * @property {'link' | 'group'} type
 * @property {ClusteredLink} [link] - For `type: 'link'`.
 * @property {string} [title] - Accordion header for `type: 'group'`.
 * @property {ClusteredLink[]} [links] - For `type: 'group'`.
 */

/**
 * @typedef {Object} ClusteredLinksSection
 * @property {string} title
 * @property {string} [icon]
 * @property {ClusteredLink | null} [titleLink]
 * @property {ClusteredLinksItem[]} items
 */

/**
 * @property {string} [anchorId]
 * @property {ClusteredLinksSection[]} sections
 */
export default class ClusteredLinks extends TemplateElement {
    constructor() {
        super({ styles: [style] });
    }

    properties() {
        return {
            anchorId: '',
            sections: [],
        };
    }

    _renderSectionTitle(section) {
        const { title, titleLink, icon } = section;
        const hasTitle = !!title || (titleLink && titleLink.text);
        if (!icon && !hasTitle) return null;

        const titleText = titleLink?.text || title;
        const label = titleLink?.url
            ? html`<a
                  href="${titleLink.url}"
                  target="${titleLink.type === 'external' ? '_blank' : '_self'}"
                  tracking-label="${titleLink.trackingLabel || ''}"
                  class="text-green-medium no-underline hover:underline"
              >
                  ${titleText}
              </a>`
            : html`<span>${titleText}</span>`;

        return html`
            <h3 class="flex items-center gap-12 m-0 fix-title2 font-medium text-green-medium">
                ${when(icon, html`<mb-icon name="${icon}" class="size-32 shrink-0"></mb-icon>`)}
                ${when(hasTitle, label)}
            </h3>
        `;
    }

    _renderLinkRow(link, extraClass = '') {
        const target = link.type === 'external' || link.type === 'download' ? '_blank' : '_self';
        return html`
            <a
                href="${link.url}"
                target="${target}"
                tracking-label="${link.trackingLabel || ''}"
                class="${link.cssClasses ||
                ''} block fix-body1 text-green-medium no-underline hover:underline ${extraClass}"
            >
                ${link.text}
            </a>
        `;
    }

    _renderItem(item) {
        const cardClass = 'block bg-white gap-8 border-b border-[var(--mdx-sys-color-primary-subtle3)]';

        if (item.type === 'group') {
            const links = item.links || [];
            if (links.length === 0) return null;
            return html`
                <mb-accordion-item variant="minimal" class="${cardClass}">
                    <div slot="header" class="p-16 fix-body1 text-green-medium">${item.title}</div>
                    <div slot="content" class="flex flex-col">
                        ${links.map((link) => this._renderLinkRow(link, 'pl-32 pr-16 mb-16'))}
                    </div>
                </mb-accordion-item>
            `;
        }

        if (!item.link?.url) return html``;
        return html`<div class="${cardClass}">${this._renderLinkRow(item.link, 'p-16')}</div>`;
    }

    template() {
        const sections = this.sections || [];
        return html`
            <div
                id="${this.anchorId || ''}"
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-fix-m gap-x-fix-m gap-y-flex-m"
            >
                ${sections.map(
                    (section) => html`
                        <div class="flex flex-col gap-12">
                            ${this._renderSectionTitle(section)}
                            <mb-accordion-group show-multiple="false" class="flex flex-col">
                                ${(section.items || []).map((item) => this._renderItem(item))}
                            </mb-accordion-group>
                        </div>
                    `,
                )}
            </div>
        `;
    }
}

defineElement('mb-clustered-links', ClusteredLinks);
